import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Truck, 
  Pickaxe, 
  Tractor, 
  HardHat, 
  Ship, 
  Plane, 
  Zap, 
  Factory, 
  Fuel, 
  Building2, 
  TreePine, 
  Droplet,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { industries, expandedSectors } from '../mock';

export const Industries = () => {
  const iconMap = {
    'TruckIcon': Truck,
    'Pickaxe': Pickaxe,
    'Tractor': Tractor,
    'HardHat': HardHat,
    'Ship': Ship,
    'Plane': Plane,
    'Zap': Zap,
    'Factory': Factory,
    'Fuel': Fuel,
    'Building2': Building2,
    'TreePine': TreePine,
    'Droplet': Droplet
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white text-base px-4 py-1">Industries We Serve</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Powering Africa's Key Industries</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Specialized fuel solutions tailored to {industries.length}+ industries across Southern Africa
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => {
              const Icon = iconMap[industry.icon] || Truck;
              
              return (
                <Card key={industry.id} className="overflow-hidden border-2 border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={industry.image} 
                      alt={industry.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="h-12 w-12 rounded-lg bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-lg">
                        <Icon className="h-6 w-6 text-emerald-600" />
                      </div>
                    </div>
                  </div>
                  
                  <CardHeader>
                    <CardTitle className="text-xl">{industry.name}</CardTitle>
                    <p className="text-gray-600 text-sm">{industry.description}</p>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-2">
                      {industry.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Complete Sectors List */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-teal-100 text-teal-700">Complete Coverage</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">All Sectors We Support</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our fuel solutions extend across diverse sectors throughout Southern Africa
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {expandedSectors.map((sector, index) => (
              <div 
                key={index}
                className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-emerald-400 hover:shadow-md transition-all duration-300 flex items-center gap-3"
              >
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <span className="text-gray-800 font-medium">{sector}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Power Your Industry?</h2>
          <p className="text-xl mb-8 text-emerald-50">
            Get a customized fuel solution tailored to your sector's needs
          </p>
          <Link to="/contact">
            <button className="inline-flex items-center gap-2 bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-50 transition-all duration-300 hover:shadow-xl hover:scale-105">
              Request a Quote <ArrowRight className="h-5 w-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;
